export interface CorporateInvestorClassificationItem {
	id: number;
	accountType: string;
	investorType: string;
	label: string;
	status: string;
	sortOrder: number;
}

export type CorporateInvestorClassificationsMap = Record<
	string,
	CorporateInvestorClassificationItem[]
>;

export type InvestorClassificationOption = {
	label: string;
	value: string;
	isDisabled?: boolean;
};

const toOption = (
	item: CorporateInvestorClassificationItem,
	isDisabled = false,
): InvestorClassificationOption => ({
	label: item.label,
	value: String(item.id),
	isDisabled,
});

/** Active dropdown options for the current investor type. */
export const getInvestorClassificationOptions = (
	investorType: string | undefined,
	corporateInvestorClassifications: CorporateInvestorClassificationsMap = {},
): InvestorClassificationOption[] => {
	if (!investorType) return [];

	return (corporateInvestorClassifications[investorType] ?? [])
		.filter((item) => item.status === "ACTIVE")
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((item) => toOption(item));
};

export const areCorporateClassificationIdsActive = (
	selectedIds: number[] = [],
	investorType: string | undefined,
	corporateInvestorClassifications: CorporateInvestorClassificationsMap = {},
): boolean => {
	if (!selectedIds.length) return true;
	if (!investorType) return true;

	const activeOptions = getInvestorClassificationOptions(
		investorType,
		corporateInvestorClassifications,
	);
	if (!activeOptions.length) return true;

	const activeIds = new Set(activeOptions.map((option) => Number(option.value)));
	return selectedIds.every((id) => activeIds.has(Number(id)));
};

/** Map saved criteria IDs to react-select option objects for the controlled value. */
export const getSelectedInvestorClassificationOptions = (
	selectedIds: number[] = [],
	investorType: string | undefined,
	corporateInvestorClassifications: CorporateInvestorClassificationsMap = {},
	savedLabels: string[] = [],
	savedIds: number[] = [],
): InvestorClassificationOption[] => {
	if (!selectedIds.length) return [];

	const activeOptions = getInvestorClassificationOptions(
		investorType,
		corporateInvestorClassifications,
	);
	const allItems = Object.values(corporateInvestorClassifications).flat();
	const profileIds = savedIds.length ? savedIds : selectedIds;

	return selectedIds
		.map((id) => {
			const activeOption = activeOptions.find(
				(option) => Number(option.value) === Number(id),
			);
			if (activeOption) return activeOption;

			const inactiveItem = allItems.find((item) => Number(item.id) === Number(id));
			if (inactiveItem) return toOption(inactiveItem, true);

			const savedIndex = profileIds.findIndex(
				(profileId) => Number(profileId) === Number(id),
			);
			const savedLabel = savedIndex >= 0 ? savedLabels[savedIndex] : undefined;
			if (savedLabel) {
				return { label: savedLabel, value: String(id), isDisabled: true };
			}

			return null;
		})
		.filter((option): option is InvestorClassificationOption => option !== null);
};
